import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useFontSize } from './FontSizeContext';
import { 
  Save, ArrowLeft, User, Wrench, Shield, Search, Plus, Trash2, List, 
  DollarSign, ChevronLeft, ChevronRight, Camera, UserCircle
} from 'lucide-react';
import UniversalLookup from './UniversalLookup';
import AuditHistoryModal from './AuditHistoryModal';
import SmartPrintButton from './PrintButton';

const LaborDetails = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const [activeTab, setActiveTab] = useState('MAIN');
  const fileInputRef = useRef(null);
  
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';  
  
  const [data, setData] = useState({
    laborcode: '', personid: '', craft_code: '', status: 'ACTIVE', actual_rate: 0,
    org_id: sessionOrg, site_id: sessionSite, image_url: '',
    person: { displayname: '', email: '', title: '' },
    crafts_list: [] 
  });
  
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
  const [lookup, setLookup] = useState({ isOpen: false, field: '', type: '', targetIdx: null, title: '', filters: {} });
  const [showAudit, setShowAudit] = useState(false);

  const loadData = useCallback(async () => {
    if (isNew) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/labor/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setData(res.data);
      
      // جلب النافيجيشن
      const navRes = await axios.get(`/api/labor/${id}/navigation${location.search}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNav(navRes.data);
    } catch (err) { 
      toast.error(t('common.load_error', "Error loading record")); 
    }
  }, [id, isNew, location.search, t]);

  useEffect(() => { loadData(); }, [loadData]);

  // 🎯 المؤهلات/الشهادات (Qualifications) - جدول مستقل ليها endpoints
  // خاصة بيها (مش جزء من الـ payload العام لحفظ العامل)، عشان كده مالهاش
  // علاقة بزرار "SAVE" الرئيسي - كل إضافة/حذف بتتنفذ وتتخزن فورًا
  const [qualifications, setQualifications] = useState([]);
  const [newQual, setNewQual] = useState({ qualification: '', description: '', expiration_date: '' });

  const fetchQualifications = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await axios.get(`/api/labor/${id}/qualifications`);
      setQualifications(res.data || []);
    } catch (err) {
      console.error("Failed to load qualifications", err);
    }
  }, [id, isNew]);

  useEffect(() => { fetchQualifications(); }, [fetchQualifications]);

  const handleAddQualification = async () => {
    if (!newQual.qualification.trim()) {
      return toast.error(t('labor.qualifications.codeRequired', 'Qualification code is required'));
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/labor/${id}/qualifications`, newQual, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNewQual({ qualification: '', description: '', expiration_date: '' });
      fetchQualifications();
      toast.success(t('common.save_success', 'Saved Successfully'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.save_fail', 'Save Failed'));
    }
  };

  const handleDeleteQualification = async (qualId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/labor-qualifications/${qualId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setQualifications(prev => prev.filter(q => q.id !== qualId));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.delete_fail', 'Delete Failed'));
    }
  };

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  const handleSave = async () => {
    const loadId = toast.loading(t('common.saving', "Saving..."));
    try {
      const token = localStorage.getItem('token'); 
      await axios.post('/api/labor/save', data, { headers: { 'Authorization': `Bearer ${token}` } });
      toast.success(t('common.save_success', "Saved successfully"), { id: loadId });
      if (isNew) navigate(`/labors/${data.laborcode}${location.search}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.save_fail', "Save failed"), { id: loadId });
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: t('common.are_you_sure', 'Are you sure?'),
      text: t('labor.details.delete_warn', 'You will not be able to recover this record!'),
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33',
      confirmButtonText: t('common.yes_delete', 'Yes, delete it!'),
      customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]` }
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/labor/${data.laborcode}`, { headers: { 'Authorization': `Bearer ${token}` } });
        toast.success(t('common.delete_success', "Deleted successfully"));
        navigate(`/labors${location.search}`); 
      } catch (err) {
        toast.error(err.response?.data?.detail || t('common.delete_fail', "Delete failed"));
      }
    }
  };

  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || isNew) return; 
    
    const formData = new FormData();
    formData.append('file', file);
    
    const loadId = toast.loading(t('common.uploading', "Uploading image..."));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`/api/labor/${data.laborcode}/upload-image`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setData({ ...data, image_url: res.data.url });
      toast.success(t('common.upload_success', "Image uploaded!"), { id: loadId });
    } catch (err) {
      toast.error(t('common.upload_fail', "Upload failed"), { id: loadId });
    }
  };

  const goTo = (targetId) => targetId && navigate(`/labors/${targetId}${location.search}`);

  const handleSearch = (fieldName, title, defaultType, lineIdx = null) => {
    let extraFilters = { site_id: data.site_id, org_id: data.org_id };
    if (lineIdx !== null && data.crafts_list && data.crafts_list[lineIdx]?.craft_code) {
      extraFilters.craft_code = data.crafts_list[lineIdx].craft_code;
    }
    setLookup({ isOpen: true, title, type: defaultType, field: fieldName, filters: extraFilters, targetIdx: lineIdx });
  };

  const addNewCraftRow = () => setData({ ...data, crafts_list: [...(data.crafts_list || []), { craft_code: '', skill_level: '', standard_rate: 0, is_default: false }] });
  const removeCraftRow = (index) => {
    const newList = [...(data.crafts_list || [])];
    newList.splice(index, 1);
    setData({ ...data, crafts_list: newList });
  };

  return (
    <div className={`flex flex-col h-screen bg-surface-app tracking-tighter uppercase ${isRTL ? 'font-cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* --- 1. TOP TOOLBAR (Control Center) --- */}
      <div className="bg-primary-dark text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4">
        <div className={`flex items-center gap-3 ${isRTL ? 'mr-2' : 'ml-2'}`}>
          <button onClick={() => navigate(`/labors${location.search}`)} className="p-1.5 hover:bg-white/10 rounded-full text-white/70 transition-colors">
            {isRTL ? <ChevronRight size={18}/> : <ArrowLeft size={18}/>}
          </button>

          {!isNew && (
            <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1">
                <span className="text-status-warning font-black">{nav.current_pos}</span> / {nav.total}
              </div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}
        </div>

        <div className={`flex items-center gap-2 ${isRTL ? 'ml-2' : 'mr-2'}`}>
          <button onClick={handleSave} className="bg-status-success hover:bg-status-success text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] font-black shadow-md transition-all">
            <Save size={14}/> {t('common.save', 'SAVE')}
          </button>

          <div className="w-[1px] h-5 bg-white/20 mx-1"></div>
          <SmartPrintButton moduleName="LABOR" refId={data.laborcode} />

          {!isNew && (
            <>
              <div className="w-[1px] h-5 bg-white/20 mx-1"></div>
              <button onClick={handleDelete} className="p-1.5 text-status-danger hover:bg-status-danger/20 rounded transition-colors" title={t('common.delete')}>
                <Trash2 size={16}/>
              </button>
            </>
          )}
        </div>
      </div>

      {/* --- SPLIT LAYOUT (SIDEBAR + MAIN CONTENT) --- */}
      <div className="flex flex-1 overflow-hidden">
        
        {/* 🎯 SIDEBAR (PROFILE CARD) */}
        <div className={`w-80 bg-surface-card border-line z-10 flex flex-col items-center py-8 px-6 shadow-[0_0_15px_rgba(0,0,0,0.03)] ${isRTL ? 'border-l' : 'border-r'}`}>

          {/* Avatar/Image Section */}
          <div className="relative group mb-6">
            <div className="w-44 h-44 rounded-full border-4 border-white shadow-xl overflow-hidden bg-surface-panel flex items-center justify-center ring-1 ring-line-subtle">
              {data.image_url ? (
                <img src={data.image_url} alt="Labor" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <UserCircle size={80} className="text-ink-secondary" strokeWidth={1} />
              )}
            </div>
            {!isNew && (
              <div 
                className="absolute inset-0 bg-black/50 rounded-full flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-all duration-300 backdrop-blur-sm border-4 border-transparent"
                onClick={() => fileInputRef.current.click()}
              >
                <Camera size={32} className="text-white mb-2 shadow-lg" />
                <span className="text-white text-[10px] font-black tracking-widest uppercase">{t('common.upload', 'UPLOAD')}</span>
              </div>
            )}
            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />
          </div>

          {/* Labor Basic Info */}
          <div className="w-full space-y-5 text-center">
            <div className="flex flex-col">
              <label className="text-[9px] text-ink-secondary font-black uppercase tracking-widest mb-1">{t('labor.fields.laborcode', 'LABOR CODE')}</label>
              <input
                className={`text-2xl font-black text-primary-dark outline-none border-b-2 border-dashed border-line focus:border-primary bg-transparent uppercase text-center transition-colors pb-1 ${!isNew && 'opacity-80 border-transparent'}`}
                value={data.laborcode}
                onChange={e => setData({...data, laborcode: e.target.value.toUpperCase()})}
                disabled={!isNew}
                placeholder="NEW_LABOR"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-[9px] text-ink-secondary font-black uppercase tracking-widest mb-1">{t('labor.fields.fullname', 'PERSON NAME')}</label>
              <span className="text-primary font-black text-lg truncate px-2" title={data.person?.displayname}>{data.person?.displayname || '---'}</span>
            </div>

            {/* Status Badge */}
            <div className="pt-2">
              <span className={`px-5 py-1.5 rounded-full text-[10px] shadow-sm border font-black uppercase tracking-widest ${ data.status === 'ACTIVE' ? 'bg-status-success-bg text-status-success border-status-success/30' : 'bg-status-danger-bg text-status-danger border-status-danger/30' }`}>
                {data.status || 'INACTIVE'}
              </span>
            </div>
          </div>
        </div>

        {/* 🎯 MAIN CONTENT AREA (TABS + DATA) */}
        <div className="flex-1 flex flex-col bg-surface-app overflow-hidden">

          {/* TABS LAYOUT */}
          <div className="flex bg-surface-card shadow-sm overflow-x-auto no-scrollbar px-4 pt-2">
            {['MAIN', 'CRAFTS', 'QUALIFICATIONS', 'LOG'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-8 py-3 border-b-4 transition-all flex items-center gap-2 text-[10px] font-black uppercase tracking-widest whitespace-nowrap rounded-t-md
                ${activeTab === tab ? 'border-status-warning text-primary-dark bg-status-warning-bg/30' : 'border-transparent text-ink-secondary hover:text-ink hover:bg-surface-panel'}`}>
                {tab === 'CRAFTS' && <Wrench size={13}/>} {tab === 'QUALIFICATIONS' && <Shield size={13}/>} {t(`labor.tabs.${tab.toLowerCase()}`, tab)}
              </button>
            ))}
          </div>

          {/* CONTENT AREA */}
          <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">
            <div className="max-w-5xl mx-auto">
              {activeTab === 'MAIN' && (
                <div className="bg-surface-card rounded-lg shadow-sm p-8 animate-in fade-in duration-500 border border-line-subtle">
                  <div className="grid grid-cols-2 gap-16">
                    <div className="space-y-6">
                      <h3 className="text-primary-dark border-b border-line-subtle pb-3 flex items-center gap-2 font-black italic text-sm">
                        <div className="w-1.5 h-4 bg-primary rounded-full"></div> {t('labor.sections.basic', 'Basic Information')}
                      </h3>

                      <div className="flex flex-col border-b border-line-subtle group focus-within:border-primary transition-all py-2">
                        <label className="text-ink-secondary text-[10px] font-bold tracking-widest">{t('labor.fields.personid', 'Person Reference')}</label>
                        <div className={`flex justify-between items-center h-7 mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span className="text-primary font-black text-[12px]">{data.personid || '---'}</span>
                          <Search size={16} className="text-primary cursor-pointer hover:scale-125 transition-transform" onClick={() => handleSearch('personid', t('labor.lookup.person'), 'persons')}/>
                        </div>
                      </div>

                      <div className="flex flex-col border-b border-line-subtle group focus-within:border-primary transition-all py-2">
                        <label className="text-ink-secondary text-[10px] font-bold tracking-widest">{t('labor.fields.primary_craft', 'Primary Craft')}</label>
                        <div className={`flex justify-between items-center h-7 mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span className="text-primary font-black text-[12px]">{data.craft_code || '---'}</span>
                          <Search size={16} className="text-primary cursor-pointer hover:scale-125 transition-transform" onClick={() => handleSearch('craft_code', t('labor.lookup.craft'), 'crafts')}/>
                        </div>
                      </div>

                      <div className="flex flex-col border-b border-line-subtle group focus-within:border-primary transition-all py-2">
                        <label className="text-ink-secondary text-[10px] font-bold tracking-widest">{t('labor.fields.actual_rate', 'Actual Rate (Hourly)')}</label>
                        <div className={`flex items-center gap-2 h-7 mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <DollarSign size={14} className="text-status-success" />
                          <input
                            type="number"
                            className={`outline-none text-primary font-black bg-transparent w-full text-[12px] ${isRTL ? 'text-right' : 'text-left'}`}
                            value={data.actual_rate}
                            onChange={e => setData({...data, actual_rate: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <h3 className="text-primary-dark border-b border-line-subtle pb-3 flex items-center gap-2 font-black italic text-sm">
                        <div className="w-1.5 h-4 bg-status-warning rounded-full"></div> {t('labor.sections.status', 'Status & Organization')}
                      </h3>
                      <div className="p-6 bg-surface-panel rounded-xl border border-line space-y-4">
                          <div className="flex justify-between items-center border-b border-line pb-3">
                            <span className="text-ink-secondary font-bold">{t('common.status', 'Current Status')}:</span>
                            <select
                                className="bg-surface-card text-primary px-3 py-1 rounded font-black outline-none border border-line shadow-sm cursor-pointer hover:border-primary transition-colors"
                                value={data.status}
                                onChange={(e) => setData({...data, status: e.target.value})}
                            >
                                <option value="ACTIVE">ACTIVE</option>
                                <option value="INACTIVE">INACTIVE</option>
                            </select>
                          </div>
                          <div className="flex justify-between items-center border-b border-line pb-3">
                            <span className="text-ink-secondary font-bold">{t('common.site', 'Site ID')}:</span>
                            <span className="text-primary font-black bg-surface-card px-3 py-1 rounded border border-line shadow-sm">{data.site_id || '---'}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-ink-secondary font-bold">{t('common.org', 'Organization')}:</span>
                            <span className="text-primary font-black bg-surface-card px-3 py-1 rounded border border-line shadow-sm">{data.org_id || '---'}</span>
                          </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'CRAFTS' && (
                <div className="bg-surface-card rounded-lg shadow-sm overflow-hidden animate-in slide-in-from-right duration-300 border border-line-subtle">
                  <div className="bg-surface-panel p-4 border-b border-line flex justify-between items-center">
                    <h3 className="text-primary flex items-center gap-2 font-black italic text-[12px]">
                      <List size={16} className="text-status-warning"/> {t('labor.sections.additional_crafts', 'Additional Crafts & Specialized Skills')}
                    </h3>
                    <button onClick={addNewCraftRow} className="bg-primary-dark text-white px-5 py-2 rounded text-[10px] flex items-center gap-2 hover:bg-primary transition-all shadow-md font-black active:scale-95">
                      <Plus size={14}/> {t('labor.actions.add_craft', 'ADD CRAFT LINE')}
                    </button>
                  </div>
                  <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                    <thead className="bg-surface-panel border-b border-line text-ink-secondary font-black uppercase tracking-widest">
                      <tr>
                        <th className="p-4 w-12 text-center">#</th>
                        <th className="p-4 border-x border-line">{t('labor.fields.craft', 'Craft Definition')}</th>
                        <th className="p-4 border-x border-line">{t('labor.fields.skill', 'Skill Level')}</th>
                        <th className="p-4 border-x border-line text-center">{t('labor.fields.rate', 'Hourly Rate')}</th>
                        <th className="p-4 border-x border-line text-center">{t('labor.fields.primary', 'Primary?')}</th>
                        <th className="p-4 w-16 text-center">{t('common.actions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line-subtle">
                      {data.crafts_list?.map((row, idx) => (
                        <tr key={idx} className="hover:bg-primary-soft/30 transition-colors group font-bold">
                          <td className="p-4 text-ink-secondary italic text-center">{idx + 1}</td>
                          <td className="p-4 border-x border-line-subtle text-primary">
                            <div className={`flex justify-between items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <span className="truncate">{row.craft_code || '---'}</span>
                              <Search size={16} className="text-primary/70 cursor-pointer hover:text-primary opacity-0 group-hover:opacity-100 transition-all hover:scale-125"
                                      onClick={() => handleSearch('row_craft', t('labor.lookup.craft'), 'crafts', idx)}/>
                            </div>
                          </td>
                          <td className="p-4 border-x border-line-subtle">
                            <div className={`flex justify-between items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <span className={row.skill_level ? 'text-status-warning' : 'text-ink-secondary'}>{row.skill_level || 'NOT SET'}</span>
                                <Search size={16} className="text-status-warning/70 cursor-pointer hover:text-status-warning opacity-0 group-hover:opacity-100 transition-all hover:scale-125"
                                        onClick={() => handleSearch('row_skill', t('labor.lookup.skill'), 'craftskills', idx)}/>
                            </div>
                          </td>
                          <td className="p-4 border-x border-line-subtle text-center font-mono text-status-success bg-status-success-bg/30">
                            <div className={`flex items-center justify-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <DollarSign size={12} className="text-status-success"/>
                              <input
                                type="number"
                                className="w-24 bg-transparent outline-none text-center font-black text-[12px]"
                                value={row.standard_rate || 0}
                                onChange={e => {
                                  const newList = [...data.crafts_list];
                                  newList[idx].standard_rate = parseFloat(e.target.value) || 0;
                                  setData({...data, crafts_list: newList});
                                }}
                              />
                            </div>
                          </td>
                          <td className="p-4 border-x border-line-subtle text-center">
                            <input
                              type="checkbox"
                              className="w-5 h-5 cursor-pointer accent-status-warning"
                              checked={row.is_default || false}
                              onChange={e => {
                                const isChecked = e.target.checked;
                                const newList = [...data.crafts_list];
                                newList.forEach((item, i) => item.is_default = (i === idx ? isChecked : false));
                                const newBaseData = { ...data, crafts_list: newList };
                                if (isChecked) {
                                  newBaseData.actual_rate = row.standard_rate || 0;
                                  newBaseData.craft_code = row.craft_code;
                                }
                                setData(newBaseData);
                              }}
                            />
                          </td>
                          <td className="p-4 text-center">
                            <button onClick={() => removeCraftRow(idx)} className="text-ink-secondary hover:text-status-danger transition-colors hover:bg-status-danger-bg p-1.5 rounded-md">
                              <Trash2 size={18}/>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(!data.crafts_list || data.crafts_list.length === 0) && (
                    <div className="p-20 text-center text-ink-secondary italic font-black uppercase tracking-widest bg-surface-panel/50">
                      {t('labor.messages.no_crafts', 'No Craft Associations Found')}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'QUALIFICATIONS' && (
                <div className="bg-surface-card rounded-lg shadow-sm overflow-hidden animate-in slide-in-from-right duration-300 border border-line-subtle">
                  <div className="bg-surface-panel p-4 border-b border-line">
                    <h3 className="text-primary flex items-center gap-2 font-black italic text-[12px] mb-3">
                      <Shield size={16} className="text-status-warning"/> {t('labor.sections.qualifications', 'Qualifications & Certifications')}
                    </h3>
                    {/* 🎯 عند تكليف العامل ده على أمر شغل بسطر عمالة مخطط
                    عليه qualification، الباك إند بيرفض التكليف لو مش حاصل
                    هنا على نفس الكود ساري (شوف get_missing_qualifications) */}
                    <div className={`flex items-end gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="flex flex-col gap-1">
                        <label className="text-ink-secondary text-[9px] font-bold tracking-widest">{t('labor.qualifications.code', 'Qualification Code')}</label>
                        <input
                          className="border-b-2 border-primary outline-none px-2 py-1 text-[12px] font-black text-primary uppercase bg-transparent w-40"
                          placeholder={t('labor.qualifications.codePlaceholder', 'e.g. HV-SAFETY')}
                          value={newQual.qualification}
                          onChange={e => setNewQual({ ...newQual, qualification: e.target.value.toUpperCase() })}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-ink-secondary text-[9px] font-bold tracking-widest">{t('common.description', 'Description')}</label>
                        <input
                          className="border-b border-line outline-none px-2 py-1 text-[12px] font-bold text-ink bg-transparent w-64"
                          value={newQual.description}
                          onChange={e => setNewQual({ ...newQual, description: e.target.value })}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-ink-secondary text-[9px] font-bold tracking-widest">{t('labor.qualifications.expiry', 'Expiration Date')}</label>
                        <input
                          type="date"
                          className="border-b border-line outline-none px-2 py-1 text-[12px] font-bold text-ink bg-transparent"
                          value={newQual.expiration_date}
                          onChange={e => setNewQual({ ...newQual, expiration_date: e.target.value })}
                        />
                      </div>
                      <button onClick={handleAddQualification} className="bg-primary-dark text-white px-5 py-2 rounded text-[10px] flex items-center gap-2 hover:bg-primary transition-all shadow-md font-black active:scale-95">
                        <Plus size={14}/> {t('common.add', 'ADD')}
                      </button>
                    </div>
                  </div>
                  <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                    <thead className="bg-surface-panel border-b border-line text-ink-secondary font-black uppercase tracking-widest">
                      <tr>
                        <th className="p-4 border-x border-line">{t('labor.qualifications.code', 'Qualification Code')}</th>
                        <th className="p-4 border-x border-line">{t('common.description', 'Description')}</th>
                        <th className="p-4 border-x border-line text-center">{t('labor.qualifications.expiry', 'Expiration Date')}</th>
                        <th className="p-4 w-16 text-center">{t('common.actions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line-subtle">
                      {qualifications.map(q => {
                        const isExpired = q.expiration_date && new Date(q.expiration_date) < new Date();
                        return (
                          <tr key={q.id} className="hover:bg-primary-soft/30 transition-colors font-bold">
                            <td className="p-4 border-x border-line-subtle text-primary">{q.qualification}</td>
                            <td className="p-4 border-x border-line-subtle text-ink-secondary">{q.description || '---'}</td>
                            <td className={`p-4 border-x border-line-subtle text-center ${isExpired ? 'text-status-danger' : 'text-ink'}`}>
                              {q.expiration_date ? new Date(q.expiration_date).toLocaleDateString() : t('labor.qualifications.noExpiry', 'No Expiry')}
                              {isExpired && <span className="ms-2 text-[9px] uppercase">({t('labor.qualifications.expired', 'Expired')})</span>}
                            </td>
                            <td className="p-4 text-center">
                              <button onClick={() => handleDeleteQualification(q.id)} className="text-ink-secondary hover:text-status-danger transition-colors hover:bg-status-danger-bg p-1.5 rounded-md">
                                <Trash2 size={18}/>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {qualifications.length === 0 && (
                    <div className="p-20 text-center text-ink-secondary italic font-black uppercase tracking-widest bg-surface-panel/50">
                      {t('labor.qualifications.none', 'No Qualifications Recorded')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Lookup System */}
      <UniversalLookup 
        {...lookup} 
        isOpen={lookup.isOpen} 
        onClose={() => setLookup({...lookup, isOpen:false})}
        onSelect={(row) => {
          if (lookup.field === 'personid') {
            setData({ ...data, personid: row.personid, person: row });
          } 
          else if (lookup.field === 'row_craft') {
            const newList = [...data.crafts_list];
            if (row.skills && row.skills.length > 0) {
              const skillsToAdd = row.skills.map(s => ({
                craft_code: row.craft_code,
                skill_level: s.skill_level,
                standard_rate: s.standard_rate,
                is_default: false
              }));
              newList.splice(lookup.targetIdx, 1, ...skillsToAdd);
              setData({ ...data, crafts_list: newList });
            } else {
              newList[lookup.targetIdx] = {
                ...newList[lookup.targetIdx],
                craft_code: row.craft_code,
                standard_rate: row.standard_rate || 0
              };
              setData({ ...data, crafts_list: newList });
            }
          } 
          else if (lookup.field === 'row_skill') {
            const newList = [...data.crafts_list];
            newList[lookup.targetIdx].skill_level = row.skill_level;
            newList[lookup.targetIdx].standard_rate = row.standard_rate;
            setData({ ...data, crafts_list: newList });
          }
          else {
            setData({ ...data, [lookup.field]: row.craft_code || row.value });
          }
          setLookup({ ...lookup, isOpen: false });
        }}
      />
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="labor" recordId={data.laborcode} />
    </div>
  );
};

export default LaborDetails;